import {
    Alert, Box, Chip, CircularProgress, Snackbar,
    TablePagination, Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';
import Popup from '../Popup';
import GwOtaSessionDetail from './GwOtaSessionDetail';

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_COMPLETE' || stage === 'FLASH_SUCCESS') return 'success';
    if (['OTA_FAILED', 'REQUEST_REJECTED', 'DOWNLOAD_FAILED', 'ABORT_REJECTED'].includes(stage)) return 'error';
    if (stage === 'ABORT_ACCEPTED') return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

const COLUMNS = [
    { field: 'otaId', headerName: 'OTA ID', flex: 1.2, sortable: false },
    { field: 'stage', headerName: 'Stage', flex: 0.8, sortable: false,
      renderCell: (p) => <Chip label={p.value ?? '—'} color={stageColor(p.value)} size="small" /> },
    { field: 'forceUpdate', headerName: 'Force', flex: 0.4, sortable: false,
      renderCell: (p) => p.value ? 'Yes' : 'No' },
    { field: 'retryCount', headerName: 'Retries', flex: 0.4, sortable: false },
    { field: 'updatedBy', headerName: 'By', flex: 0.7, sortable: false,
      renderCell: (p) => p.value ?? '—' },
    { field: 'createdAt', headerName: 'Started', flex: 1, sortable: false,
      renderCell: (p) => p.value ? moment(p.value).format('DD-MM-YYYY HH:mm:ss') : '—' },
];

function GwOtaHistory({ gatewayId }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [selectedOtaId, setSelectedOtaId] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });

    const fetchSessions = () => {
        setLoading(true);
        ApiServices.getGwSelfOtaSessions(gatewayId, page, pageSize)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setSessions([]);
                    setTotal(0);
                } else {
                    setSessions(response.data.content ?? []);
                    setTotal(response.data.totalElements ?? 0);
                }
            })
            .catch(error => setSnack({ open: true, severity: 'error', message: error.response?.data ?? error.message }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchSessions();
    }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
            </Snackbar>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>
            ) : (
                <DataGrid
                    rows={sessions}
                    columns={COLUMNS}
                    getRowId={(row) => row.id}
                    autoHeight
                    hideFooter
                    disableColumnMenu
                    disableSelectionOnClick
                    onRowClick={(p) => { setSelectedOtaId(p.row.otaId); setOpenDetail(true); }}
                    sx={{ cursor: 'pointer' }}
                    slots={{
                        noRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="text.secondary">No GW OTA sessions found</Typography>
                            </Box>
                        )
                    }}
                />
            )}

            {openDetail && (
                <Popup
                    title={`GW OTA: ${selectedOtaId}`}
                    openPopup={openDetail}
                    setOpenPopup={setOpenDetail}
                >
                    <GwOtaSessionDetail otaId={selectedOtaId} />
                </Popup>
            )}
        </Box>
    );
}

export default GwOtaHistory;